/**
 * measuredPageBreaks — empirical (measured) pagination for the print /
 * PDF export path.
 *
 * The estimate-based plan (`computeTemplatePageBreaks`) decides
 * page-break-before placement from heuristic page counts
 * (`estimateSectionPages`). On real reports those estimates drift: a
 * section estimated at 2 pages might render in 0.6 pages, so the
 * estimate-derived break leaves the prior page ~60% empty and pushes a
 * lone heading (or the page footer) onto a near-blank sheet — the
 * "orphaned footer / mostly-empty page" class of bug.
 *
 * This module replaces the estimate with a MEASURED simulation. It runs
 * in the live print DOM (where every section has a real rendered
 * height), walks the sections in document order, and tracks a running
 * cursor of consumed height on the current page. A section gets a
 * page-break-before ONLY when:
 *
 *   - it is a hard page-pin / explicit break (honoured verbatim), OR
 *   - it genuinely will not fit in the space remaining on the current
 *     page (i.e. cursor + sectionHeight would overflow the A4 content
 *     box) AND the current page already carries content.
 *
 * A section that fits in the remaining space flows onto the same page —
 * no break — eliminating the whitespace gaps the estimate produced.
 *
 * Geometry: the A4 content box is the sheet height minus the print
 * margins. Puppeteer renders with `margin: { top: 20mm, bottom: 24mm }`
 * (see `render-pdf`), and `@page { margin: 1.5cm }` governs Cmd+P. We
 * accept the content height in px from the caller (computed from the
 * live page) so this module stays pure + unit-testable; the caller
 * derives it from the actual sheet + margin at print time.
 *
 * Contract / invariants:
 *   - Pure given its inputs (the measured-height map + the hard-break
 *     set + the content height). No DOM access here — the caller
 *     measures and stamps. This keeps the heuristic testable in jsdom.
 *   - A section taller than a full page never blocks the walk: it
 *     consumes its own page(s) and the cursor resets after it.
 *   - The cover is never assigned a measured break (it owns page 1 via
 *     its own `page-break-after`).
 *   - Hard breaks (template page-pin / `section.page >= 2`) always
 *     win — measured flow can ADD breaks where content overflows but
 *     never REMOVES an author-pinned break.
 */

import { REPORT_PAGE_MARGIN_MM } from '../reportLayoutTokens';

/** A4 portrait sheet height at 96dpi: 297mm × 3.7795px/mm ≈ 1122.5px. */
export const A4_SHEET_HEIGHT_PX = 1122.5;
/** A4 portrait sheet width at 96dpi: 210mm × 3.7795px/mm ≈ 793.7px. */
export const A4_SHEET_WIDTH_PX = 793.7;
/** px per mm at 96dpi — the browser print zoom baseline. */
export const PX_PER_MM = 3.779527559;

/**
 * Default print margins (mm) — derived from the SINGLE source of truth
 * {@link REPORT_PAGE_MARGIN_MM} (14/14/16/14) so the measured-break engine
 * sizes the SAME content box Chromium uses at print time.
 *
 * RRQ DEPLOY-BLOCKER FIX (C-2/E-2): this was previously a stale hardcoded
 * `{top:20,bottom:24,left:18,right:18}`, while the customer PDF prints with
 * `@page` margin 14/14/16/14 (`preferCSSPageSize:true`). The ~14mm (~53px)
 * per-page under-estimate made `computeMeasuredBreaks` decide sections
 * overflowed ~53px sooner than they really do, stamping forced breaks on the
 * wrong section starts and emitting footer-only blank sheets. Deriving from the
 * authoritative token kills the under-estimate; no second copy of the value.
 */
export const DEFAULT_PRINT_MARGINS_MM = {
  top: REPORT_PAGE_MARGIN_MM.top,
  bottom: REPORT_PAGE_MARGIN_MM.bottom,
  left: REPORT_PAGE_MARGIN_MM.left,
  right: REPORT_PAGE_MARGIN_MM.right,
};

/**
 * Derive the printable content-box height in px for a given sheet +
 * margins. This is the height available for flowing section content on
 * one page (sheet minus top + bottom margins).
 */
export function contentHeightPx(
  sheetHeightPx: number = A4_SHEET_HEIGHT_PX,
  margins: { top: number; bottom: number } = DEFAULT_PRINT_MARGINS_MM,
): number {
  const marginPx = (margins.top + margins.bottom) * PX_PER_MM;
  return Math.max(100, sheetHeightPx - marginPx);
}

export interface MeasuredSection {
  /** Section id (matches `data-section` / `data-section-id`). */
  id: string;
  /** Rendered height in px (from getBoundingClientRect). */
  heightPx: number;
}

export interface MeasuredBreakInput {
  /** Sections in document order, with their measured heights. */
  sections: MeasuredSection[];
  /** Printable content-box height per page in px. */
  contentHeightPx: number;
  /** Section ids that MUST start a new page (author page-pins / explicit
   *  `section.page`). These always receive a break (except a leading
   *  cover). Measured flow can add more but never drops these. */
  hardBreakIds?: Set<string>;
  /** Section ids that own a full page on their own (e.g. the cover) and
   *  must not be charged a measured break. */
  fullPageIds?: Set<string>;
}

export interface MeasuredBreakResult {
  /** Section ids that should receive `page-break-before: always`. */
  breakBeforeSectionIds: Set<string>;
  /** Simulated total page count (1-based; ≥1 when any content). */
  totalPages: number;
}

/**
 * Walk the measured sections and decide page breaks by simulated flow.
 *
 * Algorithm (single forward pass, O(n)):
 *   cursor = 0            // consumed height on the current page
 *   page   = 1
 *   for each section in order:
 *     if hard-break (and not the first content section): break, reset
 *     else if cursor>0 and cursor + h > contentHeight: break, reset
 *     consume h (clamp tall sections so cursor never silently inflates)
 *
 * "cursor>0" guards against breaking before the very first section on a
 * fresh page — a section taller than a whole page still starts where it
 * is rather than wasting the page above it.
 */
export function computeMeasuredBreaks(input: MeasuredBreakInput): MeasuredBreakResult {
  const breakBeforeSectionIds = new Set<string>();
  const hard = input.hardBreakIds ?? new Set<string>();
  const fullPage = input.fullPageIds ?? new Set<string>();
  const pageH = Math.max(100, input.contentHeightPx);

  let cursor = 0;
  let page = 1;
  let seenContent = false;

  for (const sec of input.sections) {
    const h = Math.max(0, Number.isFinite(sec.heightPx) ? sec.heightPx : 0);

    // A full-page owner (cover) sits on its own page; reset the cursor
    // after it without charging a measured break, and never break before
    // a leading cover.
    if (fullPage.has(sec.id)) {
      cursor = 0;
      page += 1;
      seenContent = true;
      continue;
    }

    const wantsHardBreak = hard.has(sec.id) && seenContent;

    // William QA 2026-06-29 — sections FLOW and FILL pages. ONLY a genuine author
    // page-pin (`wantsHardBreak`) stamps a forced page-break. The former `overflows`
    // term (`cursor + h > pageH`) pushed EVERY section that did not WHOLLY fit the
    // remaining space onto a fresh sheet, leaving 60-90% blank tails (the wasted-
    // whitespace the user flagged — page 4 ~80% blank, etc.). Now an overflowing
    // section spans onto the next sheet(s) and the following section continues after
    // it, so pages fill. Atomic blocks (charts/tables/figures via break-inside:avoid)
    // still move whole; only the section-level forced break is removed.
    if (wantsHardBreak) {
      breakBeforeSectionIds.add(sec.id);
      cursor = 0;
      page += 1;
    }

    // Consume the section's height FROM the current cursor (flow). A section that
    // crosses one or more page boundaries advances the page counter by the number
    // of full sheets crossed; the cursor lands at the remainder so the NEXT section
    // flows after this one rather than getting a redundant break.
    const end = cursor + h;
    if (end > pageH) {
      const spanned = Math.floor(end / pageH);
      page += spanned;
      cursor = end - spanned * pageH;
    } else {
      cursor = end;
    }
    seenContent = true;
  }

  return { breakBeforeSectionIds, totalPages: Math.max(1, page) };
}

/**
 * Read measured section heights from a live DOM root in document order.
 * Walks `[data-section]` wrappers (the canonical section attribute the
 * print stylesheet keys off), skipping nested duplicates so a section
 * inside another section isn't double-counted.
 *
 * Returns [] in non-DOM contexts (SSR / vitest jsdom collapses rects to
 * 0; the caller gates on a real print viewport).
 */
export function readMeasuredSections(root: ParentNode | null | undefined): MeasuredSection[] {
  if (!root || typeof (root as Element).querySelectorAll !== 'function') return [];
  const wrappers = Array.from(root.querySelectorAll<HTMLElement>('[data-section]'));
  const out: MeasuredSection[] = [];
  for (const el of wrappers) {
    // Skip sections nested inside another [data-section] — only count
    // top-level section blocks so heights aren't summed twice.
    if (el.parentElement?.closest('[data-section]')) continue;
    const id = el.getAttribute('data-section');
    if (!id) continue;
    const rect = el.getBoundingClientRect();
    out.push({ id, heightPx: rect.height });
  }
  return out;
}
